import { forwardRef, type InputHTMLAttributes, type ReactNode } from 'react'

import { cn } from '@/lib/cn'

export interface CheckboxProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'size'> {
  label?: ReactNode
  description?: ReactNode
}

/**
 * A checkbox that follows the theme.
 *
 * The native box is kept (it is what assistive tech and the keyboard already
 * understand) and drawn over: `appearance-none` plus a checkmark that only
 * shows when checked. That is the whole trick, and it keeps every native
 * behaviour including indeterminate and form participation.
 */
export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(function Checkbox(
  { label, description, className, ...props },
  ref,
) {
  const box = (
    <span className="relative inline-flex shrink-0 items-center justify-center">
      <input
        ref={ref}
        type="checkbox"
        className={cn(
          'peer h-4 w-4 appearance-none rounded-sm border border-line-strong bg-surface-raised',
          'transition-[background-color,border-color] duration-100 ease-out',
          'hover:border-accent-ring',
          'checked:border-accent checked:bg-accent',
          'disabled:cursor-not-allowed disabled:opacity-50',
          className,
        )}
        {...props}
      />
      <svg
        viewBox="0 0 12 12"
        className="pointer-events-none absolute h-3 w-3 text-white opacity-0 peer-checked:opacity-100"
        aria-hidden="true"
      >
        <path
          d="M2.5 6.2l2.3 2.3L9.5 3.8"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.9"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </span>
  )

  if (!label) return box

  return (
    <label className="flex cursor-pointer items-start gap-2.5 select-none">
      <span className="mt-0.5">{box}</span>
      <span className="min-w-0 flex-1">
        <span className="block text-base text-ink">{label}</span>
        {description ? (
          <span className="block text-xs text-ink-muted">{description}</span>
        ) : null}
      </span>
    </label>
  )
})
